import '@fontsource-variable/inter';
import {
    ArrowRight,
    BriefcaseBusiness,
    Building2,
    Check,
    ChevronsLeft,
    ChevronsRight,
    createIcons,
    Drill,
    Factory,
    FileText,
    Gavel,
    Menu,
    Newspaper,
    PackageSearch,
    RefreshCw,
    ShieldCheck,
    ShoppingCart,
    Star,
    Store,
    Truck,
    UserRound,
    Wrench,
    X,
} from 'lucide';
import '../css/landing.css';

function initializeLandingMenu(): void {
    const menu = document.querySelector<HTMLDetailsElement>('[data-wop-menu]');
    const toggle = menu?.querySelector<HTMLElement>('summary');
    const closeButton = menu?.querySelector<HTMLButtonElement>('[data-wop-menu-close]');
    const panel = menu?.querySelector<HTMLElement>('.wop-landing-menu');

    if (!menu || !toggle || !closeButton || !panel) {
        return;
    }

    let closeTimer: number | undefined;

    const updateToggleLabel = (): void => {
        toggle.setAttribute(
            'aria-label',
            menu.open ? menu.dataset.closeLabel ?? 'Close menu' : menu.dataset.openLabel ?? 'Open menu',
        );
    };

    const closeMenu = (returnFocus = false): void => {
        if (!menu.open || menu.classList.contains('is-closing')) {
            return;
        }

        const finishClosing = (): void => {
            window.clearTimeout(closeTimer);
            menu.open = false;
            menu.classList.remove('is-closing');

            if (returnFocus) {
                toggle.focus();
            }
        };

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            finishClosing();
            return;
        }

        menu.classList.add('is-closing');
        panel.addEventListener('animationend', finishClosing, { once: true });
        closeTimer = window.setTimeout(finishClosing, 320);
    };

    menu.addEventListener('toggle', updateToggleLabel);
    toggle.addEventListener('click', (event) => {
        if (menu.open) {
            event.preventDefault();
            closeMenu();
        }
    });
    closeButton.addEventListener('click', () => closeMenu(true));
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.open) {
            closeMenu(true);
        }
    });
    document.addEventListener('pointerdown', (event) => {
        if (menu.open && event.target instanceof Node && !menu.contains(event.target)) {
            closeMenu();
        }
    });

    updateToggleLabel();
}

function initializePartnersCarousel(): void {
    const viewport = document.querySelector<HTMLElement>('[data-wop-partners-scroll]');
    const controls = document.querySelectorAll<HTMLButtonElement>('[data-wop-partners-direction]');

    if (!viewport || controls.length === 0) {
        return;
    }

    const updateCarouselState = (): void => {
        const isScrollable = viewport.scrollWidth > viewport.clientWidth + 1;

        viewport.classList.toggle('is-scrollable', isScrollable);
        controls.forEach((control) => {
            control.disabled = !isScrollable;
        });
    };

    controls.forEach((control) => {
        control.addEventListener('click', () => {
            const direction = control.dataset.wopPartnersDirection === 'previous' ? -1 : 1;
            const distance = Math.min(viewport.clientWidth * 0.72, 900);

            viewport.scrollBy({
                left: direction * distance,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
        });
    });

    updateCarouselState();

    if ('ResizeObserver' in window) {
        new ResizeObserver(updateCarouselState).observe(viewport);
    } else {
        window.addEventListener('resize', updateCarouselState);
    }
}

createIcons({
    icons: {
        ArrowRight,
        BriefcaseBusiness,
        Building2,
        Check,
        ChevronsLeft,
        ChevronsRight,
        Drill,
        Factory,
        FileText,
        Gavel,
        Menu,
        Newspaper,
        PackageSearch,
        RefreshCw,
        ShieldCheck,
        ShoppingCart,
        Star,
        Store,
        Truck,
        UserRound,
        Wrench,
        X,
    },
});

initializeLandingMenu();
initializePartnersCarousel();
